import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, LineChart, Line, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { Card } from '../../ui-v2';

/**
 * Gráficos para Informes — todos los charts en un archivo.
 *
 * Usa recharts (responsive, sin canvas, accesible).
 * Color primary corporativo (#003c66) protagonista.
 */

const COLOR_PRIMARY = '#003c66';
const COLOR_EMERALD = '#10b981';
const COLOR_AMBER = '#f59e0b';
const COLOR_SLATE = '#cbd5e1';

const COLOR_AMBITO = {
  interno: COLOR_PRIMARY,
  colaborador: COLOR_EMERALD,
};

// ----------------------------------------------------------------
//  Citas por mes (barras apiladas)
// ----------------------------------------------------------------

export function GraficoCitasMensuales({ data }) {
  const datos = data.map((d) => ({
    mes: formatMes(d.mes),
    Realizadas: d.realizadas,
    'En curso': d.reservadas,
    'No asistió': d.no_asistio,
    Canceladas: d.canceladas,
  }));

  return (
    <Card className="p-5">
      <h3 className="text-sm font-medium text-slate-900">Citas por mes</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-4">
        Distribución por estado en el período seleccionado.
      </p>
      {data.length === 0 ? (
        <EstadoVacio />
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={datos} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="Realizadas" stackId="a" fill={COLOR_PRIMARY} />
              <Bar dataKey="En curso" stackId="a" fill={COLOR_EMERALD} />
              <Bar dataKey="No asistió" stackId="a" fill={COLOR_AMBER} />
              <Bar dataKey="Canceladas" stackId="a" fill={COLOR_SLATE} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------
//  Tasa de no_asistio mensual (línea)
// ----------------------------------------------------------------

export function GraficoNoAsistio({ data }) {
  // Filtrar meses con muestra suficiente
  const datos = data
    .filter((d) => d.tasa_pct !== null)
    .map((d) => ({
      mes: formatMes(d.mes),
      tasa: d.tasa_pct,
      muestra: d.muestra,
    }));

  return (
    <Card className="p-5">
      <h3 className="text-sm font-medium text-slate-900">Tasa de "No asistió"</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-4">
        Porcentaje mes a mes. Solo se muestran meses con mín. 5 citas terminadas.
      </p>
      {datos.length === 0 ? (
        <EstadoVacio mensaje="No hay meses con muestra suficiente." />
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={datos} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="mes" tick={{ fontSize: 11, fill: '#64748b' }} />
              <YAxis
                tick={{ fontSize: 11, fill: '#64748b' }}
                unit="%"
                domain={[0, 'dataMax + 5']}
              />
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v, name, props) => [
                  `${v}% (${props.payload.muestra} citas)`,
                  'Tasa',
                ]}
              />
              <Line
                type="monotone"
                dataKey="tasa"
                stroke={COLOR_AMBER}
                strokeWidth={2}
                dot={{ r: 4, fill: COLOR_AMBER }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------
//  Top técnicos (barras horizontales)
// ----------------------------------------------------------------

export function GraficoTopTecnicos({ data }) {
  // Cortamos nombres muy largos
  const datos = data.slice(0, 10).map((t) => ({
    nombre: abreviarNombre(t.nombre_completo, 24),
    Realizadas: t.realizadas,
    'En curso': t.total - t.realizadas - t.no_asistio - t.canceladas,
    'No asistió': t.no_asistio,
    Canceladas: t.canceladas,
  }));

  return (
    <Card className="p-5">
      <h3 className="text-sm font-medium text-slate-900">Top técnicos por carga</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-4">
        Los 10 técnicos con más citas asignadas en el período.
      </p>
      {datos.length === 0 ? (
        <EstadoVacio />
      ) : (
        <div style={{ height: Math.max(280, datos.length * 36) }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={datos}
              layout="vertical"
              margin={{ top: 5, right: 20, left: 10, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="nombre"
                tick={{ fontSize: 11, fill: '#475569' }}
                width={150}
              />
              <Tooltip contentStyle={tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar dataKey="Realizadas" stackId="a" fill={COLOR_PRIMARY} />
              <Bar dataKey="En curso" stackId="a" fill={COLOR_EMERALD} />
              <Bar dataKey="No asistió" stackId="a" fill={COLOR_AMBER} />
              <Bar dataKey="Canceladas" stackId="a" fill={COLOR_SLATE} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------
//  Distribución por ámbito (donut)
// ----------------------------------------------------------------

export function GraficoDistribucionAmbito({ data }) {
  const datos = data.map((d) => ({
    name: d.ambito === 'colaborador' ? 'Colaboradores'
        : d.ambito === 'interno'    ? 'Internos'
        : d.ambito,
    value: d.total,
    color: COLOR_AMBITO[d.ambito] || COLOR_SLATE,
  }));
  const total = datos.reduce((s, d) => s + d.value, 0);

  return (
    <Card className="p-5">
      <h3 className="text-sm font-medium text-slate-900">Distribución por ámbito</h3>
      <p className="text-xs text-slate-500 mt-0.5 mb-4">
        Citas según el tipo de campaña.
      </p>
      {total === 0 ? (
        <EstadoVacio />
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={datos}
                cx="50%"
                cy="50%"
                innerRadius={50}
                outerRadius={90}
                paddingAngle={2}
                dataKey="value"
              >
                {datos.map((d, i) => (
                  <Cell key={i} fill={d.color} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={tooltipStyle}
                formatter={(v, name) => [`${v} citas (${Math.round((v / total) * 100)}%)`, name]}
              />
              <Legend
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                formatter={(value, entry) => (
                  <span style={{ color: '#475569' }}>
                    {value} <span style={{ color: '#94a3b8' }}>({entry.payload.value})</span>
                  </span>
                )}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}

// ----------------------------------------------------------------
//  Helpers
// ----------------------------------------------------------------

function formatMes(yyyymm) {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${meses[parseInt(m, 10) - 1]} ${y.slice(2)}`;
}

function abreviarNombre(nombre, max) {
  if (!nombre) return '';
  if (nombre.length <= max) return nombre;
  return nombre.slice(0, max - 1) + '…';
}

const tooltipStyle = {
  backgroundColor: 'white',
  border: '1px solid #e2e8f0',
  borderRadius: 6,
  fontSize: 12,
  padding: 8,
};

function EstadoVacio({ mensaje = 'Sin datos en este período.' }) {
  return (
    <div className="h-72 grid place-items-center">
      <p className="text-sm text-slate-400">{mensaje}</p>
    </div>
  );
}
